import axios from 'axios';

// Determinar la URL de la API basada en el entorno
let API_URL = process.env.REACT_APP_API_URL;

if (!API_URL) {
  // Usar URL relativa al servidor actual
  // Si la aplicación se sirve desde el mismo origen, es más confiable
  const protocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
  const host = typeof window !== 'undefined' ? window.location.host : 'localhost:3003';
  
  // Si estamos en el puerto 3003 (frontend mapeado), usar localhost:5003
  // Si estamos en otro puerto, asumir que está sirviendo desde el mismo servidor
  if (host.includes('localhost:3003') || host.includes('127.0.0.1:3003')) {
    API_URL = 'http://localhost:5003/api';
  } else if (host.includes('3003')) {
    // Puerto 3003 en otro hostname
    API_URL = `${protocol}//${host.split(':')[0]}:5003/api`;
  } else {
    // Otros puertos - asumir que la API está en la misma URL con /api
    API_URL = `${protocol}//${host}/api`;
  }
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  // Always get the latest token from localStorage for each request
  const token = localStorage.getItem('authToken');
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('[API] Added Authorization header to', config.url);
  } else {
    // Remove Authorization header if no token
    delete config.headers.Authorization;
    console.log('[API] No token found for', config.url);
  }
  
  return config;
}, (error) => {
  console.error('[API] Request interceptor error:', error);
  return Promise.reject(error);
});

// Handle 401 responses (unauthorized) - only for authenticated requests
api.interceptors.response.use(
  (response) => {
    console.log('[API] Response 200 from', response.config.url);
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const token = localStorage.getItem('authToken');
      const url = error.config?.url || '';
      console.error('[API] Got 401 error from', url, 'Token exists:', !!token);
      // Only redirect if we had a token (means it expired) and it's not a login attempt
      if (token && !url.includes('/auth/login')) {
        console.log('[API] Token expired, redirecting to login');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    } else if (error.response?.status === 422) {
      console.error('[API] Got 422 error from', error.config?.url);
      console.error('[API] Response data:', error.response.data);
    } else {
      console.error('[API] Error from', error.config?.url, 'Status:', error.response?.status, 'Data:', error.response?.data);
    }
    return Promise.reject(error);
  }
);

// Connections
export const getConnections = () => api.get('/connections');
export const getConnection = (id) => api.get(`/connections/${id}`);
export const createConnection = (data) => api.post('/connections', data);
export const updateConnection = (id, data) => api.put(`/connections/${id}`, data);
export const deleteConnection = (id) => api.delete(`/connections/${id}`);
export const testConnection = (data) => api.post('/connections/test', data);

// Models
export const getModels = (connectionId) => api.get(`/connections/${connectionId}/models`);
export const getModelFields = (connectionId, model) => api.get(`/connections/${connectionId}/models/${model}/fields`);

// Records
export const getRecords = (connectionId, model, params = {}) => 
  api.get(`/connections/${connectionId}/records/${model}`, { params });

// Translations
export const getTranslations = (params = {}) => api.get('/translations', { params });
export const upsertTranslation = (data) => api.post('/translations', data);
export const deleteTranslationApi = (data) => api.delete('/translations', { data });
export const getLanguages = () => api.get('/translations/languages');
export const getNamespaces = () => api.get('/translations/namespaces');

// Learn Resources
export const getLearnResources = () => api.get('/learn');
export const getLearnResource = (id) => api.get(`/learn/${id}`);
export const createLearnResource = (data) => api.post('/learn', data);
export const updateLearnResource = (id, data) => api.put(`/learn/${id}`, data);
export const deleteLearnResource = (id) => api.delete(`/learn/${id}`);

// Dashboard - Contracts KPIs & Forecast
export const getContractsKpis = (connectionId, year = null) => {
  const url = year ? `/connections/${connectionId}/contracts/kpis?year=${year}` : `/connections/${connectionId}/contracts/kpis`;
  return api.get(url);
};
export const getContractsForecast = (connectionId) => api.get(`/connections/${connectionId}/contracts/forecast`);
export const getFinancialSummary = (connectionId, year = null) => {
  const url = year ? `/connections/${connectionId}/financial-summary?year=${year}` : `/connections/${connectionId}/financial-summary`;
  return api.get(url);
};
export const getFinancialByPartner = (connectionId, year = null) => {
  const url = year ? `/connections/${connectionId}/financial-by-partner?year=${year}` : `/connections/${connectionId}/financial-by-partner`;
  return api.get(url);
};
export const getFinancialByCategory = (connectionId, year = null) => {
  const url = year ? `/connections/${connectionId}/financial-by-category?year=${year}` : `/connections/${connectionId}/financial-by-category`;
  return api.get(url);
};

export const validateContracts = (connectionId) => {
  return api.get(`/connections/${connectionId}/validate-contracts`);
};

export const getMonthlyInvoicing = (connectionId, year = null) => {
  const url = year ? `/connections/${connectionId}/monthly-invoicing?year=${year}` : `/connections/${connectionId}/monthly-invoicing`;
  return api.get(url);
};

export const getMonthlyInvoicingContracts = (connectionId, month, type = 'customer', year = null) => {
  const params = new URLSearchParams();
  params.append('month', month);
  params.append('type', type);
  if (year) params.append('year', year);
  return api.get(`/connections/${connectionId}/monthly-invoicing-contracts?${params.toString()}`);
};

export const getContractsFinancialSummary = (connectionId, year = null, contractType = 'sale') => {
  const params = new URLSearchParams();
  if (year) params.append('year', year);
  if (contractType) params.append('contract_type', contractType);
  return api.get(`/connections/${connectionId}/contracts-financial-summary?${params.toString()}`);
};

// Analytic Accounts
export const getAnalyticAccounts = (connectionId, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/analytics/accounts/${connectionId}${queryString ? '?' + queryString : ''}`);
};

export const getAnalyticAccountDetail = (connectionId, accountId) =>
  api.get(`/analytics/accounts/${connectionId}/${accountId}`);

export const getAnalyticLines = (connectionId, accountId, params = {}) =>
  api.get(`/analytics/accounts/${connectionId}/${accountId}/lines`, { params });

export const getAnalyticSummary = (connectionId) =>
  api.get(`/analytics/summary/${connectionId}`);

export const getAccountsByState = (connectionId) =>
  api.get(`/analytics/accounts/${connectionId}/by-state`);

export const getAnalyticFinancialSummary = (connectionId) =>
  api.get(`/analytics/accounts/${connectionId}/financial-summary`);

// Product Analysis
export const getProductIssues = (connectionId, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/products/issues/${connectionId}${queryString ? '?' + queryString : ''}`);
};

export const getProductSummary = (connectionId) =>
  api.get(`/products/summary/${connectionId}`);

export const getProductsByCategory = (connectionId) =>
  api.get(`/products/by-category/${connectionId}`);

export const getProductCategories = (connectionId) =>
  api.get(`/products/categories/${connectionId}`);

export const getProductSalesImpact = (connectionId, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/products/sales-impact/${connectionId}${queryString ? '?' + queryString : ''}`);
};

// Invoice Analysis
export const getInvoiceProductAnalysis = (connectionId) =>
  api.get(`/invoices/product-analysis/${connectionId}`);

export default api;
